/**
 * Detect a Firestore permission-denied error.
 *
 * Why this matters: when a user logs out (or switches accounts in the same
 * tab) the auth token is revoked, but in-flight Firestore real-time listeners
 * on workspace-scoped collections fire one final `permission-denied` event
 * before their `unsubscribe` propagates. That error is *expected* — local
 * state has already been cleared by the auth `effect()` — and should be
 * silently swallowed rather than logged + propagated to UI error handlers.
 *
 * Use in `catchError`:
 *
 *   private handleError = (error: unknown) => {
 *     if (isPermissionDeniedError(error) && !this.isAuthenticated()) {
 *       return EMPTY;
 *     }
 *     console.error(error);
 *     return throwError(() => new Error(...));
 *   };
 *
 * Be careful: only swallow when the user is NOT authenticated. A genuine
 * permission failure on an authenticated user (e.g. wrong workspace claim,
 * Firestore rules bug) must still surface — debugging silent failures is
 * extremely painful.
 */
export function isPermissionDeniedError(error: unknown): boolean {
  if (error == null) return false;

  // 1. String form (sometimes loggers stringify the error before passing it)
  if (typeof error === 'string') {
    return error.includes('permission-denied') ||
           error.includes('Missing or insufficient permissions');
  }

  // 2. Object form — covers both real Error instances AND plain objects
  // produced by serializers (e.g. JSON.parse(JSON.stringify(firebaseError)))
  if (typeof error === 'object') {
    const e = error as { code?: unknown; name?: unknown; message?: unknown };
    if (e.code === 'permission-denied') return true;
    if (typeof e.message === 'string' && e.message.includes('Missing or insufficient permissions')) return true;
    if (e.name === 'FirebaseError' && typeof e.message === 'string' && e.message.includes('permission')) return true;
  }

  return false;
}
